import type { Address } from '@lmc/ts-common'
import { Add, Button, FormItem, Select, theme } from '@lmc/ui-kit'
import type { Option } from '@lmc/ui-kit/src/dataEntry/MultiSelect/MultiSelect'
import { Text } from '@lmc/ui-kit/src/general'
import { Divider, Form, type FormInstance, Input, Tooltip } from 'antd'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PostcodeAutofill } from 'screens/Residents/screens/ResidentProfileScreen/screens/ResidentContactDetails/FormCards/Address/PostocdeAutofill'
import { getRequiredRule } from 'screens/Rostering/utils'
import { styled } from 'styled-components'
import { commonPatterns } from 'utils/enhancedTestIds'
import { getAddressTypeOptions } from './utils'

export type AddressOption = Option<string> & { searchLabel?: string }

type AddressFormData = Record<string, unknown> & {
  newAddress?: Partial<Address> & { type?: string }
  addressId?: string
}

type AddressFormItemsProps = {
  testId?: string
  addressOptions?: AddressOption[]
  selectedResident: string
  preferredPeopleNominative?: string
  disabled?: boolean
  formData?: AddressFormData
  setFormData?: (
    data: AddressFormData | ((prev: AddressFormData) => AddressFormData)
  ) => void
  form: FormInstance
  defaultFirstAddress?: boolean
}

const AddressFormItems: FC<AddressFormItemsProps> = ({
  testId,
  addressOptions = [],
  selectedResident,
  preferredPeopleNominative,
  disabled,
  formData,
  setFormData,
  form,
  defaultFirstAddress
}) => {
  const { t } = useTranslation()
  const baseId = testId || 'address-form-items'
  const [showAddressForm, setShowAddressForm] = useState(false)
  const [isAddingAddressManually, setIsAddingAddressManually] = useState(false)

  const flags = useFeatureFlags({
    flagNames: ['rostering_admin_editing_flexibility']
  })?.data

  const flagsRosteringAdminEditingFlexibility =
    flags?.rostering_admin_editing_flexibility?.enabled

  const addressId = Form.useWatch('addressId', form)

  const filterOption = (input: string, option?: AddressOption) =>
    String(option?.searchLabel ?? option?.label ?? '')
      .toLowerCase()
      .includes(input.toLowerCase())

  const handleAddNewAddress = () => {
    setShowAddressForm(true)
  }

  const handleFieldChange = (field: string, value: string) => {
    const currentNewAddress = form.getFieldValue('newAddress') || {}

    const updatedAddress = {
      ...currentNewAddress,
      [field]: value
    }

    form.setFieldsValue({
      newAddress: updatedAddress
    })

    if (setFormData) {
      setFormData((prev) => ({
        ...prev,
        newAddress: updatedAddress
      }))
    }
  }

  const handleAutofill = (address: Partial<Address>) => {
    const formattedAddress = {
      type: formData?.newAddress?.type || address.type,
      name: address.name || '',
      street1: address.street1 || '',
      street2: address.street2 || '',
      suburb: address.suburb || '',
      state: address.state || '',
      postcode: address.postcode || ''
    }

    form.setFieldsValue({
      newAddress: formattedAddress
    })

    if (setFormData) {
      setFormData(
        (prev) =>
          ({
            ...prev,
            newAddress: formattedAddress
          }) as AddressFormData
      )
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      defaultFirstAddress &&
      addressOptions?.length > 0 &&
      selectedResident &&
      !addressId
    ) {
      const firstAddress = addressOptions[0]

      form.setFieldValue('addressId', firstAddress.value)

      if (setFormData) {
        setFormData((prev) => ({
          ...prev,
          addressId: firstAddress.value
        }))
      }
    }
  }, [
    addressOptions,
    selectedResident,
    addressId,
    defaultFirstAddress,
    form,
    setFormData
  ])

  const dropdownRender = (menu: React.ReactElement) => (
    <div>
      {menu}
      <Divider style={{ margin: '8px 0' }} />
      <StyledButton
        data-testid={commonPatterns.button(baseId, 'add-new')}
        variant="text"
        onClick={handleAddNewAddress}
        disabled={disabled || !selectedResident}
      >
        <Add />
        {t('ADD_NEW_ADDRESS')}
      </StyledButton>
    </div>
  )

  return (
    <>
      {!showAddressForm ? (
        <Tooltip
          color={theme.color.neutral.color3}
          title={
            disabled ? (
              <ContentContainer>
                <Text>
                  {flagsRosteringAdminEditingFlexibility
                    ? t('DETAIL_EDITABLE_BY_ADMIN')
                    : t('DETAIL_CANNOT_BE_EDITED')}
                </Text>
              </ContentContainer>
            ) : (
              ''
            )
          }
          open={disabled ? undefined : false}
          overlayInnerStyle={{ width: '450px', padding: '8px 16px' }}
        >
          <FormItem
            data-testid={commonPatterns.field(baseId, 'address-select')}
            name="addressId"
            label={<Text>{t('ADDRESS')}</Text>}
            rules={[getRequiredRule(t('ADDRESS'))]}
          >
            <Select
              data-testid={commonPatterns.input(baseId, 'address-select')}
              options={addressOptions}
              allowClear
              optionFilterProp="children"
              filterOption={filterOption}
              showSearch
              disabled={disabled}
              dropdownRender={dropdownRender}
            />
          </FormItem>
        </Tooltip>
      ) : (
        <>
          <FormItem
            data-testid={commonPatterns.field(baseId, 'address-type')}
            name={['newAddress', 'type']}
            label={<Text>{t('ADDRESS_TYPE')}</Text>}
            rules={[getRequiredRule(t('ADDRESS_TYPE'))]}
          >
            <Select
              data-testid={commonPatterns.input(baseId, 'address-type')}
              options={getAddressTypeOptions(t)}
              onChange={(value) => handleFieldChange('type', value)}
            />
          </FormItem>

          <ShouldShow $shouldShow={isAddingAddressManually}>
            <AddressFieldsContainer data-testid={`${baseId}-fields-container`}>
              <FormItem
                data-testid={commonPatterns.field(baseId, 'building-name')}
                name={['newAddress', 'name']}
                label={<Text>{t('BUILDING_NAME')}</Text>}
              >
                <Input
                  data-testid={commonPatterns.input(baseId, 'building-name')}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                />
              </FormItem>

              <FormItem
                data-testid={commonPatterns.field(baseId, 'address-line-1')}
                name={['newAddress', 'street1']}
                label={<Text>{t('ADDRESS_LINE_1')}</Text>}
                rules={[getRequiredRule(t('ADDRESS_LINE_1'))]}
              >
                <Input
                  data-testid={commonPatterns.input(baseId, 'address-line-1')}
                  onChange={(e) => handleFieldChange('street1', e.target.value)}
                />
              </FormItem>

              <FormItem
                data-testid={commonPatterns.field(baseId, 'address-line-2')}
                name={['newAddress', 'street2']}
                label={<Text>{t('ADDRESS_LINE_2')}</Text>}
              >
                <Input
                  data-testid={commonPatterns.input(baseId, 'address-line-2')}
                  onChange={(e) => handleFieldChange('street2', e.target.value)}
                />
              </FormItem>

              <FormItem
                data-testid={commonPatterns.field(baseId, 'town')}
                name={['newAddress', 'suburb']}
                label={<Text>{t('TOWN')}</Text>}
                rules={[getRequiredRule(t('TOWN'))]}
              >
                <Input
                  data-testid={commonPatterns.input(baseId, 'town')}
                  onChange={(e) => handleFieldChange('suburb', e.target.value)}
                />
              </FormItem>

              <FormItem
                data-testid={commonPatterns.field(baseId, 'county')}
                name={['newAddress', 'state']}
                label={<Text>{t('COUNTY')}</Text>}
              >
                <Input
                  data-testid={commonPatterns.input(baseId, 'county')}
                  onChange={(e) => handleFieldChange('state', e.target.value)}
                />
              </FormItem>

              <FormItem
                data-testid={commonPatterns.field(baseId, 'postcode')}
                name={['newAddress', 'postcode']}
                label={<Text>{t('POSTCODE')}</Text>}
                rules={[getRequiredRule(t('POSTCODE'))]}
              >
                <Input
                  data-testid={commonPatterns.input(baseId, 'postcode')}
                  onChange={(e) =>
                    handleFieldChange('postcode', e.target.value)
                  }
                />
              </FormItem>
            </AddressFieldsContainer>
          </ShouldShow>

          {!isAddingAddressManually && (
            <PostcodeAutofill
              data-testid={`${baseId}-postcode-autofill`}
              onAutofill={handleAutofill}
              fieldName={['newAddress']}
              onManuallyChoose={() => setIsAddingAddressManually(true)}
              isAddingAddressManually={isAddingAddressManually}
            />
          )}
        </>
      )}
    </>
  )
}

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const AddressFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ShouldShow = styled.div<{ $shouldShow: boolean }>`
  display: ${(props) => (props.$shouldShow ? 'contents' : 'none')};
`

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
`

export default AddressFormItems
